import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, X } from "lucide-react";
import { makeNonogram, nonogramWin } from "@/lib/nonogram";

export function NonogramGame({ n, onWin, resetKey }) {
  const { t } = useTranslation();
  const [puzzle, setPuzzle] = useState(null);
  const [grid, setGrid] = useState([]);

  const start = useCallback(() => {
    const p = makeNonogram(n);
    setPuzzle(p);
    setGrid(Array.from({ length: n }, () => Array(n).fill(0)));
  }, [n]);

  useEffect(() => { start(); }, [start, resetKey]);

  const solved = useMemo(
    () => (puzzle && grid.length ? nonogramWin(grid, n, puzzle.rowClues, puzzle.colClues) : false),
    [grid, puzzle, n]
  );
  useEffect(() => { if (solved) onWin?.(); }, [solved, onWin]);

  const cycle = (r, c) => {
    if (solved) return;
    setGrid((g) => {
      const ng = g.map((x) => x.slice());
      ng[r][c] = (ng[r][c] + 1) % 3; // 0 empty -> 1 filled -> 2 mark
      return ng;
    });
  };

  if (!puzzle || !grid.length) return null;
  const maxRowClue = Math.max(...puzzle.rowClues.map((x) => x.length));
  const maxColClue = Math.max(...puzzle.colClues.map((x) => x.length));
  const cellPx = `min(${Math.floor(70 / (n + maxRowClue))}vw, 34px)`;

  return (
    <div className="flex flex-col items-center" data-testid="nonogram-game">
      <p className="text-xs text-foreground/45 text-center max-w-md mb-6 leading-relaxed">{t("games.nonogramRules")}</p>
      <div className="inline-block" style={{ fontSize: "11px" }}>
        {/* column clues */}
        <div className="flex" style={{ marginLeft: `calc(${cellPx} * ${maxRowClue})` }}>
          {puzzle.colClues.map((clue, c) => (
            <div key={c} className="flex flex-col justify-end items-center" style={{ width: cellPx, height: `calc(${cellPx} * ${maxColClue})` }}>
              {clue.map((num, i) => (
                <span key={i} className="text-primary/70 leading-tight tabular-nums">{num}</span>
              ))}
            </div>
          ))}
        </div>
        {/* rows */}
        {grid.map((row, r) => (
          <div key={r} className="flex items-center">
            <div className="flex justify-end items-center gap-1 pr-1" style={{ width: `calc(${cellPx} * ${maxRowClue})` }}>
              {puzzle.rowClues[r].map((num, i) => (
                <span key={i} className="text-primary/70 tabular-nums">{num}</span>
              ))}
            </div>
            {row.map((v, c) => (
              <button
                key={c}
                data-testid={`nono-cell-${r}-${c}`}
                onClick={() => cycle(r, c)}
                style={{ width: cellPx, height: cellPx }}
                className={`border border-white/10 flex items-center justify-center transition-colors
                  ${v === 1 ? "bg-primary/80" : "bg-white/[0.03] hover:bg-primary/15"}`}
              >
                {v === 2 && <X size={12} className="text-foreground/40" />}
              </button>
            ))}
          </div>
        ))}
      </div>
      <button onClick={start} data-testid="nono-new-btn" className="mt-7 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-foreground/45 hover:text-primary transition-colors">
        <RefreshCw size={13} /> {t("games.newGrid")}
      </button>
    </div>
  );
}
